/**
 * GradebookPage — Phase 1.7e
 * Faculty-facing composite course gradebook. Aggregates finalized
 * submissions across ALL rubrics, weighted by category (Assignment /
 * Discussion / Quiz / Presentation / Exam). Includes a course-level
 * curve slider that operates on composite percentages.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import {
  getGradebookConfig, updateGradebookConfig, getGradebookCohort,
  courseCurvePreview, courseCurveApply, courseCurveClear,
} from '../lib/apiClient';

const CATEGORIES = ['assignment', 'discussion', 'quiz', 'presentation', 'exam', 'learnex', 'other'];
const CAT_LABEL = {
  assignment:   'Assignment',
  discussion:   'Discussion',
  quiz:         'Quiz',
  presentation: 'Presentation',
  exam:         'Exam',
  learnex:      'LearnEx',
  other:        'Other',
};

const fmtPct = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleString() : '—'; } catch { return iso; } };

const Stat = ({ label, value }) => (
  <div className="px-3 py-2 rounded bg-gray-50 border border-gray-200">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="font-mono text-sm text-gray-900">{value}</div>
  </div>
);

const GradebookPage = () => {
  const [config, setConfig] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [weightsDraft, setWeightsDraft] = useState(null);
  const [savingWeights, setSavingWeights] = useState(false);
  const [savedBanner, setSavedBanner] = useState(false);

  // Course-level curve UI state
  const [mode, setMode] = useState('bump');
  const [bump, setBump] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [tgtMean, setTgtMean] = useState(80);
  const [tgtStd, setTgtStd] = useState(8);
  const [curvePreview, setCurvePreview] = useState(null);
  const [curveBusy, setCurveBusy] = useState(false);
  const [curveErr, setCurveErr] = useState('');
  const [curveApplied, setCurveApplied] = useState(false);

  const refresh = async () => {
    try {
      const [c, k] = await Promise.all([getGradebookConfig(), getGradebookCohort()]);
      setConfig(c);
      setCohort(k);
      setWeightsDraft(c.weights);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const totalWeight = useMemo(() => {
    if (!weightsDraft) return 0;
    return CATEGORIES.reduce((s, c) => s + (Number(weightsDraft[c]) || 0), 0);
  }, [weightsDraft]);

  const saveWeights = async () => {
    setSavingWeights(true);
    try {
      const r = await updateGradebookConfig(weightsDraft);
      setSavedBanner(true);
      setTimeout(() => setSavedBanner(false), 2000);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally { setSavingWeights(false); }
  };

  const curvePayload = () => ({
    mode,
    bump_points: Number(bump) || 0,
    scale_factor: Number(scale) || 1,
    target_mean_pct: Number(tgtMean) || 0,
    target_std_pct: Number(tgtStd) || 0,
  });

  const doCurvePreview = async () => {
    setCurveBusy(true); setCurveErr(''); setCurveApplied(false);
    try {
      const r = await courseCurvePreview(curvePayload());
      setCurvePreview(r);
    } catch (e) {
      setCurveErr(e.response?.data?.detail || 'Preview failed');
    } finally { setCurveBusy(false); }
  };

  const doCurveApply = async () => {
    setCurveBusy(true); setCurveErr('');
    try {
      await courseCurveApply(curvePayload());
      setCurveApplied(true);
      await refresh();
    } catch (e) {
      setCurveErr(e.response?.data?.detail || 'Apply failed');
    } finally { setCurveBusy(false); }
  };

  const doCurveClear = async () => {
    if (!window.confirm('Clear the course-level curve from all students?')) return;
    setCurveBusy(true); setCurveErr('');
    try {
      await courseCurveClear();
      setCurvePreview(null);
      setCurveApplied(false);
      await refresh();
    } catch (e) {
      setCurveErr(e.response?.data?.detail || 'Clear failed');
    } finally { setCurveBusy(false); }
  };

  if (loading) return (
    <div className="min-h-screen"><Navbar /><div className="p-8 text-gray-500">Loading gradebook…</div></div>
  );

  const activeCourseCurve = config?.active_course_curve;

  return (
    <div className="min-h-screen" data-testid="gradebook-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider font-semibold text-emerald-700">Phase 1.7e</div>
            <h1 className="text-3xl font-bold text-gray-900 mt-1">Course Gradebook</h1>
            <p className="text-gray-500 mt-1">Composite course % per student, weighted across categories. Top-level curve applied to the composite.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/rubrics" className="btn-outline text-sm" data-testid="gradebook-rubrics-link">📝 Rubrics</Link>
            <Link to="/chat-efficiency" className="btn-outline text-sm" data-testid="gradebook-chat-link">🧭 Chat Efficiency</Link>
          </div>
        </div>

        {err && <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}

        {/* Category weights */}
        <section className="card p-5 mb-6" data-testid="weights-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="font-bold text-gray-900">Category weights</h2>
              <p className="text-xs text-gray-500">Set how each evaluation type contributes to the composite. Weights are renormalized — categories with zero submissions are skipped (their weight is redistributed).</p>
            </div>
            <div className="text-xs text-gray-600">Sum of weights: <span className="font-mono font-bold">{totalWeight.toFixed(0)}</span></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            {CATEGORIES.map((cat) => (
              <div key={cat} data-testid={`weight-${cat}`}>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{CAT_LABEL[cat]}</label>
                <input
                  type="number" min="0" step="1"
                  className="input-field"
                  value={weightsDraft?.[cat] ?? 0}
                  onChange={(e) => setWeightsDraft({ ...weightsDraft, [cat]: Number(e.target.value) || 0 })}
                  data-testid={`weight-input-${cat}`}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={saveWeights} disabled={savingWeights} className="btn-primary text-sm" data-testid="save-weights-btn">
              {savingWeights ? 'Saving…' : 'Save weights'}
            </button>
            <button
              onClick={() => setWeightsDraft(config.default_weights)}
              className="btn-outline text-sm" data-testid="reset-weights-btn"
            >
              Reset to defaults
            </button>
            {savedBanner && <span className="text-sm text-emerald-700">✓ Saved</span>}
          </div>
        </section>

        {/* Course-level curve */}
        <section className="card p-5 mb-6" data-testid="course-curve-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="font-bold text-gray-900">Course-level grade curve</h2>
              <p className="text-xs text-gray-500">Applied to the <strong>composite %</strong>, not individual rubrics. Use for end-of-term curving.</p>
            </div>
            {activeCourseCurve && (
              <span className="badge-published" data-testid="course-curve-active-pill">
                ✓ Active: {activeCourseCurve.mode}
                {activeCourseCurve.mode === 'bump' && activeCourseCurve.bump_points ? ` +${activeCourseCurve.bump_points}` : ''}
                {activeCourseCurve.mode === 'scale' && activeCourseCurve.scale_factor ? ` ×${Number(activeCourseCurve.scale_factor).toFixed(2)}` : ''}
                {activeCourseCurve.mode === 'zscore' ? ` μ=${activeCourseCurve.target_mean_pct}% σ=${activeCourseCurve.target_std_pct}%` : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Mode</label>
              <select className="input-field" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="course-curve-mode">
                <option value="bump">Bump (add %)</option>
                <option value="scale">Scale (multiply)</option>
                <option value="zscore">Z-score (target μ, σ)</option>
              </select>
            </div>
            {mode === 'bump' && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Bump (% points)</label>
                <input type="range" min="0" max="25" step="0.5" value={bump} onChange={(e) => setBump(e.target.value)} className="w-48" data-testid="course-curve-bump" />
                <div className="text-xs text-center font-mono">+{bump}%</div>
              </div>
            )}
            {mode === 'scale' && (
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Scale factor</label>
                <input type="range" min="0.5" max="1.5" step="0.05" value={scale} onChange={(e) => setScale(e.target.value)} className="w-48" data-testid="course-curve-scale" />
                <div className="text-xs text-center font-mono">×{Number(scale).toFixed(2)}</div>
              </div>
            )}
            {mode === 'zscore' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Target mean</label>
                  <input type="range" min="60" max="95" step="1" value={tgtMean} onChange={(e) => setTgtMean(e.target.value)} className="w-40" data-testid="course-curve-mean" />
                  <div className="text-xs text-center font-mono">{tgtMean}%</div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Target std</label>
                  <input type="range" min="2" max="20" step="1" value={tgtStd} onChange={(e) => setTgtStd(e.target.value)} className="w-40" data-testid="course-curve-std" />
                  <div className="text-xs text-center font-mono">{tgtStd}%</div>
                </div>
              </>
            )}
            <button onClick={doCurvePreview} disabled={curveBusy} className="btn-secondary text-sm" data-testid="course-curve-preview-btn">
              {curveBusy ? 'Working…' : 'Preview'}
            </button>
            <button onClick={doCurveApply} disabled={curveBusy || !curvePreview} className="btn-primary text-sm" data-testid="course-curve-apply-btn">
              Apply to all
            </button>
            {activeCourseCurve && (
              <button onClick={doCurveClear} disabled={curveBusy} className="text-sm text-red-600 hover:underline" data-testid="course-curve-clear-btn">
                Clear curve
              </button>
            )}
          </div>
          {curveErr && <div className="mt-2 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{curveErr}</div>}
          {curveApplied && <div className="mt-2 p-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm">Course curve applied.</div>}
          {curvePreview && (
            <div className="mt-3" data-testid="course-curve-result">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <Stat label="N" value={curvePreview.before.n} />
                <Stat label="Before mean" value={`${curvePreview.before.mean.toFixed(1)}%`} />
                <Stat label="After mean" value={`${curvePreview.after.mean.toFixed(1)}%`} />
                <Stat label="Before std" value={curvePreview.before.std.toFixed(2)} />
                <Stat label="After std" value={curvePreview.after.std.toFixed(2)} />
              </div>
            </div>
          )}
        </section>

        {/* Cohort table */}
        <section className="card overflow-hidden" data-testid="cohort-section">
          <div className="px-5 py-3 border-b flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700">
                Cohort composite ({cohort?.students?.length || 0} students)
              </h2>
              <div className="text-xs text-gray-500 mt-0.5">
                {cohort?.rubrics_considered || 0} rubrics considered ·
                cohort mean <span className="font-mono">{cohort?.cohort_stats?.mean?.toFixed(1) || '0.0'}%</span> ·
                std <span className="font-mono">{cohort?.cohort_stats?.std?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cohort-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Student</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">Submissions</th>
                  {CATEGORIES.filter((c) => c !== 'other').map((c) => (
                    <th key={c} className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">{CAT_LABEL[c]}</th>
                  ))}
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">Composite</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-gray-600">Curved</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">Last finalized</th>
                </tr>
              </thead>
              <tbody>
                {(cohort?.students || []).map((s) => (
                  <tr key={s.email} className="border-b hover:bg-emerald-50/40" data-testid={`gradebook-row-${s.email}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-gray-900">{s.name || s.email}</div>
                      {s.name && <div className="text-[11px] text-gray-500">{s.email}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono" title="Total finalized submissions across all categories. Categories with 0 weight (e.g. uncategorized rubrics) are counted here but excluded from the composite.">{s.submission_count}</td>
                    {CATEGORIES.filter((c) => c !== 'other').map((c) => {
                      const cat = s.per_category?.[c];
                      return (
                        <td key={c} className="px-3 py-2.5 text-right">
                          {cat?.avg_pct != null ? (
                            <span className="font-mono">{cat.avg_pct.toFixed(1)}%</span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                          {cat?.count > 0 && (
                            <span className="text-[10px] text-gray-400 ml-1">({cat.count})</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2.5 text-right">
                      {s.composite_pct != null ? (
                        <span className="font-mono font-bold">{s.composite_pct.toFixed(1)}%</span>
                      ) : <span className="text-gray-400">no weighted data</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {s.composite_curved != null ? (
                        <span className="font-mono font-bold text-emerald-700">{s.composite_curved.toFixed(1)}%</span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{fmtDate(s.last_finalized)}</td>
                  </tr>
                ))}
                {(!cohort?.students || cohort.students.length === 0) && (
                  <tr>
                    <td colSpan={CATEGORIES.filter((c) => c !== 'other').length + 5} className="px-3 py-8 text-center text-gray-500" data-testid="gradebook-empty">
                      No finalized submissions yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 text-xs text-gray-500 border-t bg-gray-50">
            <strong>Formula:</strong> for each category the student's % is the mean of (final_total ÷ max_total) across all finalized submissions of that category. Composite is the weighted mean across categories. When a category has no submissions, its weight is redistributed to categories that do.
          </div>
        </section>
      </main>
    </div>
  );
};

export default GradebookPage;
